import { Button, Inline, Stack, breakpoints, vars, Text, textVariants } from '@etg/wings';
import { useDirection, useProperty, useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import type { MouseEvent } from 'react';
import type { IncludedCheckedBaggage } from '@eti/schema-types';
import { ScreenReaderPrice } from '@eti/components';
import {
  getMinNumberOfSeats,
  shouldShowSeatsLeftNotification,
} from '../../../../utils/resultUtils';
import SeatsLeftInfo from '../../../../../../common/components/SeatsLeftInfo';
import type {
  AvailableExtraProductsType,
  BoundsType,
  IncludedCabinBaggageType,
} from '../../../../models';
import type {
  IncludedCabinBaggageType as RebookingTripIncludedCabinBaggageType,
  RebookingTripBoundsType,
} from '../../../../../change-trip/models';
import type { RebookingTicketProps, Ticket } from '../../drawer/model';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import { tripHasSelfTransfer } from '../../utils/trip';
import { TripBaggageInfo } from './TripBaggageInfo';

const tripBaggageAndPriceInfoWrapperStyles = (shouldHavePointerCursor: boolean) => css`
  ${shouldHavePointerCursor && `cursor: pointer;`}
  justify-content: space-between;
  padding: 8px 16px;

  @media ${mediaQueries.small.only} {
    border-top: 1px solid ${vars.colors.divider};
    padding-top: 16px;
  }
`;

const viewTripButtonStyles = css`
  width: 178px;
`;

const pricePerPersonStyles = css`
  font-size: 1.5rem;
  font-weight: 700;
`;

const desktopSeatWrapperStyles = (isRTL: boolean) => css`
  max-width: 178px;

  span {
    text-align: ${isRTL ? 'left' : 'right'};
  }
`;

const baggagePriceWrapperStyles = css`
  flex-direction: column;
  height: 100%;
  justify-content: space-between;

  @media ${mediaQueries.small.only} {
    flex-direction: row;
    width: 100%;
  }
`;

const selfTransferPromotionStyles = css`
  color: ${vars.colors.promotional.foreground};
`;

export interface TripBaggageAndPriceInfoProps {
  availableExtraProducts?: AvailableExtraProductsType;
  bounds: BoundsType | RebookingTripBoundsType;
  includedCabinBaggage: IncludedCabinBaggageType | RebookingTripIncludedCabinBaggageType;
  includedCheckedBaggage?: IncludedCheckedBaggage | null;
  isVI?: boolean;
  onButtonClick: (e: MouseEvent<HTMLElement>) => void;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  numberOfTravelers?: number;
  ticket: RebookingTicketProps | Ticket;
}

export const TripBaggageAndPriceInfo = ({
  availableExtraProducts = [],
  bounds,
  includedCabinBaggage,
  includedCheckedBaggage,
  isVI,
  onButtonClick,
  onClick,
  ticket,
  numberOfTravelers,
}: TripBaggageAndPriceInfoProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isRTL } = useDirection();
  const isMobileDevice = window.matchMedia(`(max-width: ${breakpoints._768})`).matches;
  const isVISelfTransferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const isSelfTransferInTrip = tripHasSelfTransfer(bounds) && isVISelfTransferDealEnabled;
  const numberOfSeatsLeft = getMinNumberOfSeats(bounds);
  const seatsThreshold = p('Air.Result.ShowNumberOfSeatsLeft.WhenLessThan');
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);

  const shouldShowSeatsLeftNotificationMessage =
    !isChangeTripPage &&
    shouldShowSeatsLeftNotification(numberOfSeatsLeft, numberOfTravelers, seatsThreshold);

  const { priceDisplayText, pricePerPerson } = ticket;

  return (
    <Stack
      align={isMobileDevice ? 'start' : 'end'}
      className={tripBaggageAndPriceInfoWrapperStyles(Boolean(onClick))}
      onClick={onClick}
      role={isMobileDevice && isVISelfTransferDealEnabled ? 'button' : undefined}
      spacing={isMobileDevice ? 8 : 16}
    >
      <Inline className={baggagePriceWrapperStyles}>
        <TripBaggageInfo
          availableExtraProducts={availableExtraProducts}
          bounds={bounds}
          includedCabinBaggage={includedCabinBaggage}
          includedCheckedBaggage={includedCheckedBaggage}
          isVI={isVI}
        />
        <Stack>
          <Stack align="end" className={desktopSeatWrapperStyles(isRTL)} spacing={4}>
            {isSelfTransferInTrip && (
              <Text className={selfTransferPromotionStyles} variant={textVariants._14_BOLD}>
                {t('Result.Page.SelfTransferDeals.Promotion.Text')}
              </Text>
            )}
            {shouldShowSeatsLeftNotificationMessage && !isSelfTransferInTrip && (
              <SeatsLeftInfo numberOfSeatsLeft={numberOfSeatsLeft} />
            )}
            <Stack align="end" spacing={4}>
              <p aria-hidden className={pricePerPersonStyles} data-testid="standard-price">
                {pricePerPerson}
              </p>
              <ScreenReaderPrice price={pricePerPerson} />
              <p>{priceDisplayText}</p>
            </Stack>
          </Stack>
          {!isMobileDevice && (
            <Button
              className={viewTripButtonStyles}
              data-testid="view-trip-button"
              onClick={onButtonClick}
              variant="primary"
            >
              {t('Result.Page.TripCard.Button.Text')}
            </Button>
          )}
        </Stack>
      </Inline>
    </Stack>
  );
};
